import type { AxiosRequestConfig, AxiosInstance as ApiClient } from 'axios'
import axios, { isAxiosError } from 'axios'

import type {
  RequestErrorInterceptor,
  RequestInterceptor,
  SuccessfulResponseInterceptor,
  ResponseErrorInterceptor,
} from '@/utils/axios'

export type { ApiClient }

const logOnRequest =
  (serviceName: string): RequestInterceptor =>
  config => {
    console.debug(
      `[${serviceName}] Requested: ${config.method?.toUpperCase()} ${config.baseURL}${
        config.url
      }`,
    )
    return config
  }

const logOnRequestError =
  (serviceName: string): RequestErrorInterceptor =>
  error => {
    console.error(`[${serviceName}] Requested: ${JSON.stringify(error)}`)
    return Promise.reject(error)
  }

const logOnSuccessfulResponse =
  (serviceName: string): SuccessfulResponseInterceptor =>
  response => {
    console.debug(`[${serviceName}] Received: ${response.status} ${response.statusText}`)
    return response
  }

const logOnErrorResponse =
  (serviceName: string): ResponseErrorInterceptor =>
  error => {
    if (isAxiosError(error)) {
      if (!error.response) {
        console.error(`[${serviceName}] Network error. Did not receive a response.`)
        return Promise.reject(error)
      }

      console.error(
        `[${serviceName}] Received: ${error.response.status} ${error.response.statusText}`,
      )

      return Promise.reject(error)
    }

    console.error(`[${serviceName}] An unexpected error occurred.`)

    return Promise.reject(error)
  }

export const createApiClient = (
  serviceName: string,
  config: AxiosRequestConfig,
): ApiClient => {
  const apiClient = axios.create(config)

  apiClient.interceptors.request.use(
    logOnRequest(serviceName),
    logOnRequestError(serviceName),
  )
  apiClient.interceptors.response.use(
    logOnSuccessfulResponse(serviceName),
    logOnErrorResponse(serviceName),
  )

  return apiClient
}
